import React, { useEffect, useState } from "react";
import { Button, Input, Space, Tooltip } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { MAX_LENGTH, parseLength } from "./geometry";
import { mmToInput } from "./format";

/**
 * A length in metres, stored as millimetres. Accepts "3,45", "3.45", "345 cm", "3455 mm" and
 * "3 m 45". The camera button measures the value from a photo (or live, where supported).
 */
const LengthInput = ({ value, onChange, onMeasure, readOnly, placeholder, ariaLabel }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [text, setText] = useState(mmToInput(value, lng));
  const [focused, setFocused] = useState(false);
  const [invalid, setInvalid] = useState(false);

  // Values set from outside (camera, dictation) show up unless the field is being edited.
  useEffect(() => {
    if (!focused) setText(mmToInput(value, lng));
  }, [value, focused, lng]);

  const read = raw => {
    const mm = parseLength(raw, "m");
    if (mm === null) return 0;
    if (Number.isNaN(mm) || mm > MAX_LENGTH) return NaN;
    return mm;
  };

  const handleChange = event => {
    const raw = event.target.value;
    setText(raw);
    const mm = read(raw);
    if (!Number.isNaN(mm)) {
      setInvalid(false);
      if (mm !== value) onChange(mm);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    const mm = read(text);
    setInvalid(Number.isNaN(mm));
    if (!Number.isNaN(mm)) setText(mmToInput(mm, lng));
  };

  const input = (
    <Input
      inputMode="decimal"
      value={text}
      readOnly={readOnly}
      status={invalid ? "error" : undefined}
      placeholder={placeholder ?? "0,00"}
      suffix="m"
      aria-label={ariaLabel}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      onChange={handleChange}
      onPressEnter={handleBlur}
    />
  );

  if (!onMeasure || readOnly) return input;
  return (
    <Space.Compact block>
      {input}
      <Tooltip title={t("Measure with the camera")}>
        <Button
          icon={<CameraOutlined />}
          aria-label={t("Measure with the camera")}
          onClick={() => onMeasure(mm => onChange(mm))}
        />
      </Tooltip>
    </Space.Compact>
  );
};

export default LengthInput;
